import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Loader2, Send, HandHeart } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { JOIN_INTERESTS } from "@/data/constants";
import { useLanguage } from "@/context/LanguageContext";
import { submitContact, submitJoinMovement, formatApiError } from "@/lib/api";

export default function Contact() {
  const { settings } = useOutletContext();
  const [tab, setTab] = useState("contact");
  const { tr } = useLanguage();

  return (
    <div data-testid="contact-page">
      <PageSeo
        slug="contact"
        title="Contact | Girish Kumar Naidu"
        description="Contact Girish Kumar Naidu's public service office — phone, WhatsApp, email, and office address. Reach out or join the movement."
        keywords="contact Girish Kumar Naidu, public service office Bengaluru, WhatsApp"
        schemas={[
          { "@context": "https://schema.org", "@type": "ContactPage", name: "Contact Girish Kumar Naidu" },
          breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Contact" }]),
        ]}
      />
      <PageHeader
        overline={tr("page.contact.overline")}
        title={tr("page.contact.title")}
        subtitle={tr("page.contact.subtitle")}
        crumbs={[{ name: "Contact" }]}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          {/* Info */}
          <div className="lg:col-span-5 space-y-4">
            <ContactItem icon={Phone} label="Phone" value={settings?.phone} href={`tel:${settings?.phone}`} />
            <ContactItem icon={MessageCircle} label="WhatsApp" value={settings?.whatsapp} href={`https://wa.me/${(settings?.whatsapp || "").replace(/[^\d]/g, "")}`} />
            <ContactItem icon={Mail} label="Email" value={settings?.email} href={`mailto:${settings?.email}`} />
            <ContactItem icon={MapPin} label="Office Address" value={settings?.address} />
            <div className="rounded-2xl overflow-hidden border border-gray-200 h-64 mt-6">
              <iframe
                title="Office location"
                src={settings?.mapEmbed || "https://www.google.com/maps?q=Hoodi,Bengaluru&output=embed"}
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>

          {/* Forms */}
          <div className="lg:col-span-7">
            <div className="flex gap-2 mb-8">
              <button
                onClick={() => setTab("contact")}
                data-testid="tab-contact"
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${tab === "contact" ? "bg-navy text-white" : "bg-paper border border-gray-200 text-gray-600"}`}
              >
                Contact Form
              </button>
              <button
                onClick={() => setTab("join")}
                data-testid="tab-join"
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${tab === "join" ? "bg-navy text-white" : "bg-paper border border-gray-200 text-gray-600"}`}
              >
                Join the Movement
              </button>
            </div>
            {tab === "contact" ? <ContactForm /> : <JoinForm />}
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactItem({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4 p-5 rounded-2xl bg-paper border border-gray-200 hover:border-gold/50 transition-colors">
      <div className="h-11 w-11 rounded-xl bg-navy flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-gold" />
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold">{label}</p>
        <p className="text-navy font-semibold mt-0.5">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer" className="block">{inner}</a> : inner;
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-navy mb-2">{label}</label>
      <input type={type} value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none" />
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) return toast.error("Please fill name, phone and message.");
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message);
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
      <div className="grid md:grid-cols-2 gap-5">
        <Input label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="contact-name" />
        <Input label="Phone *" value={form.phone} onChange={(v) => set("phone", v)} testid="contact-phone" />
      </div>
      <Input label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testid="contact-email" />
      <div>
        <label className="block text-sm font-semibold text-navy mb-2">Message *</label>
        <textarea rows={5} value={form.message} data-testid="contact-message" onChange={(e) => set("message", e.target.value)}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none resize-none" />
      </div>
      <button type="submit" disabled={loading} data-testid="contact-submit"
        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-white font-bold btn-saffron disabled:opacity-60">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Message
      </button>
    </form>
  );
}

function JoinForm() {
  const [form, setForm] = useState({ name: "", phone: "", locality: "", interest: JOIN_INTERESTS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Please fill name and phone.");
    setLoading(true);
    try {
      const res = await submitJoinMovement(form);
      toast.success(res.message);
      setForm({ name: "", phone: "", locality: "", interest: JOIN_INTERESTS[0], message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="join-form">
      <div className="grid md:grid-cols-2 gap-5">
        <Input label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="join-name" />
        <Input label="Phone *" value={form.phone} onChange={(v) => set("phone", v)} testid="join-phone" />
      </div>
      <Input label="Locality" value={form.locality} onChange={(v) => set("locality", v)} testid="join-locality" />
      <div>
        <label className="block text-sm font-semibold text-navy mb-2">Interest</label>
        <select value={form.interest} data-testid="join-interest" onChange={(e) => set("interest", e.target.value)}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none bg-white">
          {JOIN_INTERESTS.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-semibold text-navy mb-2">Message</label>
        <textarea rows={4} value={form.message} data-testid="join-message" onChange={(e) => set("message", e.target.value)}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none resize-none" />
      </div>
      <button type="submit" disabled={loading} data-testid="join-submit"
        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-civic text-white font-bold hover:bg-civic-dark transition-colors disabled:opacity-60">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandHeart className="h-4 w-4" />} Join the Movement
      </button>
    </form>
  );
}
